import { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from './components/Navbar';
import { API_BASE_URL } from '../config/ApiConfig';
import { Link } from 'react-router-dom';
import Pagination10 from './components/Pagination10';
import PageCard from './components/PageCard';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import usePagination10 from './components/hooks/usePagination10';
import Footer from './components/Footer';
import LoadingModal from './components/modals/LoadingModal';
import { formatDateView, formatTimeView } from './utils/dateUtils';

// Certifique-se de que o CSS para .full-screen-div ainda está sendo importado
// .full-screen-div { display: flex; flex-direction: column; min-height: 100vh; }

const initialAtaFormState = {
    cnpjEscola: '',
    data: '',
    hora: '',
    dataIni: '',
    dataFim: '',
    dataEdital: '',
    presidentePronome: '',
    presidenteNome: '',
    secretarioPronome: '',
    secretarioNome: '',
};

function Ata() {
    // ... todo o seu state, useEffects e funções (NÃO MUDA NADA AQUI) ...
    const token = localStorage.getItem('token');
    const cnpj = localStorage.getItem('cnpj');

    const [AtaForm, setAtaForm] = useState({
        ...initialAtaFormState,
        cnpjEscola: cnpj || ''
    });

    const [Atas, setAtas] = useState([]);
    const [atualizar, setAtualizar] = useState(false);

    const { setDefaultModal, showDefaultModal } = useDefaultModal();
    const { setConfirmModal, showConfirmModal } = useConfirmModal();

    const totalItems = Atas.length;
    const { setPagination } = usePagination10(totalItems); // Hook usePagination10
    const displayedAtas = Atas.slice(
        setPagination.startIndex,
        setPagination.startIndex + setPagination.rowsPerPage
    );

    const [isLoading, setLoading] = useState(true);

    useEffect(() => {
        if (!cnpj) {
            showDefaultModal('error', 'CNPJ não encontrado. Faça login novamente.');
            setLoading(false);
            return;
        }
        setLoading(true);
        axios.get(`${API_BASE_URL.ATA}/all`, {
            params: { cnpjEscola: cnpj },
            headers: { Authorization: `Bearer ${token}` }
        }).then(result => {
            setAtas(result.data);
        }).catch(error => {
            console.error('Erro ao obter registros de atas:', error);
            showDefaultModal('error', 'Erro ao carregar atas.');
        }).finally(() => {
            setLoading(false);
        });
    }, [token, atualizar, cnpj]);

    function handleChange(event) {
        event.preventDefault();
        const { name, value } = event.target;
        let processedValue = value;

        if (['presidentePronome', 'presidenteNome', 'secretarioPronome', 'secretarioNome'].includes(name)) {
            processedValue = value.toUpperCase();
        }

        const newState = {
            ...AtaForm,
            [name]: processedValue,
        };

        if (name === 'dataIni' && value) {
            try {
                const dataIni = new Date(value);

                if (!isNaN(dataIni.getTime())) {
                    // Calcula dataFim (2 anos depois)
                    const dataFim = new Date(dataIni);
                    dataFim.setFullYear(dataFim.getFullYear() + 2);
                    newState.dataFim = dataFim.toISOString().split('T')[0];

                    // Calcula dataEdital (2 dias úteis antes)
                    const dataEdital = calcularDataEditalValida(dataIni);
                    newState.dataEdital = dataEdital.toISOString().split('T')[0];
                }
            } catch (error) {
                console.error('Erro ao processar datas:', error);
            }
        }

        setAtaForm(newState);
    }

    // Função principal para calcular a data válida do edital
    function calcularDataEditalValida(dataInicio) {
        let data = new Date(dataInicio);
        let diasUteisEncontrados = 0;
        const MAX_TENTATIVAS = 10; // Segurança contra loop infinito

        // Retrocede até encontrar 2 dias úteis válidos
        while (diasUteisEncontrados < 2 && MAX_TENTATIVAS > 0) {
            data.setDate(data.getDate() - 1);

            if (isDiaUtil(data)) {
                diasUteisEncontrados++;
            }
        }

        return data;
    }

    // Verifica se é dia útil (não é fim de semana nem feriado)
    function isDiaUtil(data) {
        // Verifica fim de semana (0 = Domingo, 6 = Sábado)
        if (data.getDay() === 0 || data.getDay() === 6) {
            return false;
        }

        // Verifica feriados
        const feriados = getFeriados(data.getFullYear());
        const dataStr = formatarDataParaComparacao(data);

        return !feriados.includes(dataStr);
    }

    // Retorna todos os feriados do ano (fixos + móveis)
    function getFeriados(ano) {
        const feriadosFixos = [
            `01-01`, // Ano Novo
            `04-21`, // Tiradentes
            `05-01`, // Dia do Trabalho
            `09-07`, // Independência
            `10-12`, // Nossa Senhora Aparecida
            `11-02`, // Finados
            `11-15`, // Proclamação da República
            `12-25`  // Natal
        ];

        // Calcula feriados móveis baseados na Páscoa
        const pascoa = calcularPascoa(ano);
        const feriadosMoveis = [
            calcularDataRelativa(pascoa, -47),  // Carnaval
            calcularDataRelativa(pascoa, -2),   // Sexta-Feira Santa
            calcularDataRelativa(pascoa, 60)    // Corpus Christi
        ];

        return [...feriadosFixos, ...feriadosMoveis];
    }

    // Calcula a Páscoa (algoritmo de Gauss)
    function calcularPascoa(ano) {
        const a = ano % 19;
        const b = Math.floor(ano / 100);
        const c = ano % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const mes = Math.floor((h + l - 7 * m + 114) / 31) - 1;
        const dia = ((h + l - 7 * m + 114) % 31) + 1;

        return new Date(ano, mes, dia);
    }

    // Funções auxiliares
    function calcularDataRelativa(dataBase, dias) {
        const novaData = new Date(dataBase);
        novaData.setDate(novaData.getDate() + dias);
        return formatarDataParaComparacao(novaData);
    }

    function formatarDataParaComparacao(data) {
        return `${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
    }

    function handleSubmit(event) {
        event.preventDefault();

        const payload = {
            ...AtaForm,
            cnpjEscola: cnpj
        };

        if (!payload.cnpjEscola || !payload.data || !payload.hora) {
            showDefaultModal('error', 'CNPJ da Escola, Data da Ata e Hora da Ata são obrigatórios!');
            return;
        }

        ['dataIni', 'dataFim', 'dataEdital'].forEach(field => {
            if (payload[field] === '') {
                payload[field] = null;
            }
        });

        console.log('Salvando Ata com payload:', payload);

        axios.post(`${API_BASE_URL.ATA}/save`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(() => {
            setAtualizar(prev => !prev);
            setAtaForm({
                ...initialAtaFormState,
                cnpjEscola: cnpj
            });
            showDefaultModal('success', 'Ata salva com sucesso!');
        }).catch(error => {
            console.error('Erro ao salvar Ata:', error);
            const errorMessage = error.response?.data?.message || 'Erro ao salvar a ata.';
            showDefaultModal('error', errorMessage);
        });
    }

    function handleDelete(ataToDelete) {
        if (!ataToDelete.cnpjEscola || ataToDelete.cnpjEscola !== cnpj) {
            showDefaultModal('error', 'Não é possível excluir esta ata. CNPJ não corresponde.');
            return;
        }
        showConfirmModal('delete', () => handleConfirmDelete(ataToDelete));
    }

    function handleConfirmDelete(ataToDelete) {
        const dataPk = ataToDelete.data;
        const horaPk = ataToDelete.hora;

        axios.delete(`${API_BASE_URL.ATA}/delete`, {
            params: {
                cnpjEscola: ataToDelete.cnpjEscola,
                data: dataPk,
                hora: horaPk
            },
            headers: { Authorization: `Bearer ${token}` }
        }).then(() => {
            setAtualizar(prev => !prev);
            showDefaultModal('success', 'Ata excluída com sucesso!');
        }).catch(error => {
            console.error('Erro ao deletar Ata:', error);
            const errorMessage = error.response?.data?.message || 'Erro ao excluir a ata.';
            showDefaultModal('error', errorMessage);
        });
    }

    async function handlePrintAta(ataToPrint) {
        setLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL.ATA}/preencherAta`,
                ataToPrint,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob',
                }
            );
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `Ata.docx`;
            link.click();
        } catch (error) {
            console.error('Erro ao preencher o template:', error);
            showDefaultModal('error', 'Erro ao preencher o template.');
        } finally {
            setLoading(false);
        }
    }

    async function handlePrintEdital(ataToPrint) {
        setLoading(true);
        try {
            const response = await axios.post(
                `${API_BASE_URL.ATA}/preencherEdital`,
                ataToPrint,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    responseType: 'blob',
                }
            );
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `Edital.docx`;
            link.click();
        } catch (error) {
            console.error('Erro ao preencher o template:', error);
            showDefaultModal('error', 'Erro ao preencher o template.');
        } finally {
            setLoading(false);
        }
    }


    return (
        // 1. O div principal perde seus estilos de padding e background
        <div className='full-screen-div'>
            <Navbar />

            {/* 2. Adicionamos o "wrapper" para o background */}
            <div
                style={{
                    flex: '1 0 auto',
                    backgroundImage: `url('/assets/desktop.png')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed',
                    overflowY: 'auto'
                }}
            >
                {/* 3. O container do conteúdo vai aqui dentro, mantendo seu espaçamento para a navbar */}
                <div className="container mt-2 pt-5 small">
                    <PageCard title="Ata" icon="bi-journal-text" embedded
                        className="container mt-4 mb-0 px-3 py-3" // Removido 'no-transparency'
                        style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)' }} // Adicionado fundo semi-transparente
                    >
                        <div className="d-flex justify-content-between align-items-center pt-1 pb-2">
                            <h4 className="text-secondary text-shadow text-success">GERENCIAMENTO DE ATAS</h4>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {/* ... Seu formulário ... */}
                            <div className="row">
                            <div className='col-md-2 mb-3 input-group-sm'>
                                <label className='form-label'>Data da Ata</label>
                                <input type="date" className='form-control' name='data' value={AtaForm.data} onChange={handleChange} required />
                            </div>
                            <div className='col-md-2 mb-3 input-group-sm'>
                                <label className='form-label'>Hora da Ata</label>
                                <input type="time" className='form-control' name='hora' value={AtaForm.hora} onChange={handleChange} required />
                            </div>
                            <div className='col-md-2 mb-3 input-group-sm'>
                                <label className='form-label'>Data Início</label>
                                <input type="date" className='form-control' name='dataIni' value={AtaForm.dataIni} onChange={handleChange} />
                            </div>
                            <div className='col-md-2 mb-3 input-group-sm'>
                                <label className='form-label'>Data Fim</label>
                                <input type="date" className='form-control' name='dataFim' value={AtaForm.dataFim} onChange={handleChange} />
                            </div>
                            <div className='col-md-2 mb-3 input-group-sm'>
                                <label className='form-label'>Data Edital</label>
                                <input type="date" className='form-control' name='dataEdital' value={AtaForm.dataEdital} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="row">
                            <div className='col-md-7 mb-3 input-group-sm'>
                                <label className='form-label'>Nome Presidente</label>
                                <input type="text" className='form-control' name='presidenteNome' value={AtaForm.presidenteNome} onChange={handleChange} maxLength="70" />
                            </div>
                            <div className='col-md-3 mb-3 input-group-sm'>
                                <label className='form-label'>Pronome</label>
                                <select
                                    className="form-select"
                                    name="presidentePronome"
                                    value={AtaForm.presidentePronome}
                                    onChange={handleChange}
                                >
                                    <option value="">Selecione...</option>
                                    <option value="SR.">SR.</option>
                                    <option value="SRA.">SRA.</option>
                                    <option value="SRTA.">SRTA.</option>
                                </select>
                            </div>
                        </div>
                        <div className="row">
                            <div className='col-md-7 mb-3 input-group-sm'>
                                <label className='form-label'>Nome Secretário</label>
                                <input type="text" className='form-control' name='secretarioNome' value={AtaForm.secretarioNome} onChange={handleChange} maxLength="70" />
                            </div>
                            <div className='col-md-3 mb-3 input-group-sm'>
                                <label className='form-label'>Pronome</label>
                                <select
                                    className="form-select"
                                    name="secretarioPronome"
                                    value={AtaForm.secretarioPronome}
                                    onChange={handleChange}
                                >
                                    <option value="">Selecione...</option>
                                    <option value="SR.">SR.</option>
                                    <option value="SRA.">SRA.</option>
                                    <option value="SRTA.">SRTA.</option>
                                </select>
                            </div>
                        </div>

                        <div className='col-md-12 mt-2 mb-3 input-group-sm'>
                            <Link to="/home" className='btn btn-secondary mx-1'>
                                <i className="bi bi-arrow-left" /> Voltar
                            </Link>
                            <button type="submit" className='btn btn-success mx-1'>
                                <i className="bi bi-check-lg" /> Salvar Ata
                            </button>
                        </div>
                        </form>
                        <LoadingModal loading={isLoading} />
                        <div className="table-responsive-sm shadow-lg mb-2" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                            <table className="table table-hover table-striped table-sm small">
                                <thead className="table-secondary sticky-top bg-light">
                                    <tr>
                                        <th scope="col">Data</th>
                                        <th scope="col">Hora</th>
                                        <th scope="col">Data Início</th>
                                        <th scope="col">Data Fim</th>
                                        <th scope="col">Data Edital</th>
                                        <th scope="col">Presidente</th>
                                        <th scope="col">Secretário</th>
                                        <th scope="col" className="text-center align-middle">Ação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!isLoading && displayedAtas.length > 0 ? (displayedAtas.map(ata => (
                                        <tr key={`${ata.cnpjEscola}-${ata.data}-${ata.hora}`}>
                                            <td>{formatDateView(ata.data)}</td>
                                            <td>{formatTimeView(ata.hora)}</td>
                                            <td>{formatDateView(ata.dataIni)}</td>
                                            <td>{formatDateView(ata.dataFim)}</td>
                                            <td>{formatDateView(ata.dataEdital)}</td>
                                            <td>{ata.presidenteNome || '-'}</td>
                                            <td>{ata.secretarioNome || '-'}</td>
                                            <td className="text-center">
                                                <button onClick={() => handlePrintAta(ata)} className="btn btn-outline-success btn-sm mx-1 my-1">
                                                    <i className="bi bi-printer me-1" /> Imprimir Ata
                                                </button>
                                                <button onClick={() => handlePrintEdital(ata)} className="btn btn-outline-success btn-sm mx-1 my-1">
                                                    <i className="bi bi-printer me-1" /> Imprimir Edital
                                                </button>
                                                <button onClick={() => handleDelete(ata)} className='btn btn-outline-danger btn-sm mx-1 my-1'>
                                                    <i className="bi bi-trash-fill" /> Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))) : (
                                        <tr>
                                            <td colSpan="8" className="text-center">{isLoading ? 'Carregando...' : 'Nenhuma ata encontrada.'}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <Pagination10 {...setPagination} />
                        <ConfirmModal {...setConfirmModal} />
                        <DefaultModal {...setDefaultModal} />
                    </PageCard>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default Ata;